import React, {
  forwardRef,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useTranslate } from 'nucleus-text';
// TODO HUB-152725
// eslint-disable-next-line no-restricted-imports
import { VariableSizeList as List } from 'react-window';
// eslint-disable-next-line no-restricted-imports
import AutoSizer from 'react-virtualized-auto-sizer';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import { useRouter } from 'next/router';
import {
  useRemoveAppointment,
  useAcceptAppointment
} from '@components/appointments/utilities/useAppointmentMutations';
import NotificationContent from '@components/notifications/notificationFlyout/NotificationContent';
import { appointmentFactOrigins } from '@components/appointments/constants';
import { DECLINE_APPOINTMENT } from '@graphql/operations/appointment/declineAppointment.ts';
import { notificationPropType } from '../notificationPropType';

const getErrorResx = error => {
  return error?.graphQLErrors?.[0]?.errorInfo?.additionalInfo?.response
    ?.errors?.[0]?.resx;
};

const getOuterElementType = notificationsListLength => {
  const outerElementType = forwardRef((props, ref) => {
    const { translate } = useTranslate();
    return (
      <ul
        ref={ref}
        {...props}
        role="group"
        aria-label={translate('Notifications-List-Length-Aria', {
          count: notificationsListLength
        })}
        style={{
          ...props.style,
          ...{ padding: 0, margin: 0, listStyleType: 'none' }
        }}
      />
    );
  });
  return outerElementType;
};

const NotificationsContainer = ({
  notification,
  testID,
  onClose,
  eventId,
  markAndPublishNotificationsAsRead,
  containerRef,
  onNotificationVisible,
  surveyUrl,
  setSize,
  index
}) => {
  const router = useRouter();

  const [isApptAccepted, setIsAcceptedAppt] = useState(false);
  const [isApptDeclined, setIsApptDeclined] = useState(false);
  const apptId = notification.metadata?.entity?.entityId;

  const locale = useLocaleForQueries();

  const handleError = error => {
    onClose();
    router.push(
      `/events/${eventId}/schedule?apptId=${apptId}&error=${getErrorResx(
        error
      )}`
    );
  };

  const readNotificationWithDelay = () => {
    setTimeout(() => {
      markAndPublishNotificationsAsRead([notification.id]);
    }, 4000);
  };

  const { removeAppt: declineMutation, removing: isDeclining } =
    useRemoveAppointment(
      DECLINE_APPOINTMENT,
      apptId,
      () => {
        setIsApptDeclined(true);
        readNotificationWithDelay();
      },
      handleError,
      locale,
      appointmentFactOrigins.APPOINTMENT_NOTIFICATION
    );

  const { acceptAppointment: acceptMutation, accepting: isAccepting } =
    useAcceptAppointment(
      () => {
        setIsAcceptedAppt(true);
        readNotificationWithDelay();
      },
      handleError,
      locale,
      appointmentFactOrigins.APPOINTMENT_NOTIFICATION
    );

  return (
    <NotificationContent
      notification={{
        ...notification,
        publishedAt: new Date(notification.publishedAt)
      }}
      testID={testID}
      onClose={onClose}
      eventId={eventId}
      markAndPublishNotificationsAsRead={markAndPublishNotificationsAsRead}
      containerRef={containerRef}
      onNotificationVisible={onNotificationVisible}
      surveyUrl={surveyUrl}
      index={index}
      setSize={setSize}
      appointment={{
        acceptMutation,
        isAccepted: isApptAccepted,
        declineMutation,
        isDeclined: isApptDeclined,
        isAccepting,
        isDeclining
      }}
    />
  );
};
/**
 * Component to display notification flyout content
 */
const NotificationFlyoutContent = ({
  testID,
  content: notifications = [],
  onClose = () => null,
  eventId,
  markAndPublishNotificationsAsRead,
  rootRef,
  onNotificationVisible,
  surveyUrl
}) => {
  const sizeMap = useRef({});
  const notificationsLegthRef = useRef(notifications.length);
  const listRef = useRef({});
  const setSize = useCallback((index, size) => {
    listRef?.current.resetAfterIndex(0);
    sizeMap.current = { ...sizeMap.current, [index]: size };
  }, []);
  const getSize = useCallback(index => {
    return sizeMap.current[index] || 50;
  }, []);

  useEffect(() => {
    if (notifications.length !== notificationsLegthRef.current) {
      notificationsLegthRef.current = notifications.length;
      listRef?.current.resetAfterIndex(0);
    }
  }, [notifications.length]);

  const resizeList = useCallback(() => {
    if (listRef.current?.resetAfterIndex) {
      listRef?.current.resetAfterIndex(0);
    }
  }, [listRef]);
  useEffect(() => {
    if (listRef.current?.resetAfterIndex) {
      listRef?.current.resetAfterIndex(0);
    }
  }, [listRef]);

  const outerElementType = useMemo(() => {
    return getOuterElementType(notifications.length);
  }, [notifications.length]);

  return (
    <AutoSizer onResize={resizeList}>
      {({ width, height }) => (
        <List
          height={height || 1}
          itemCount={notifications.length}
          itemSize={getSize}
          width={width || 1}
          innerElementType={outerElementType}
          ref={listRef}
        >
          {({ index, style }) => {
            return (
              <li
                style={style}
                key={notifications[index].id}
                role="listitem"
                tabIndex={0}
              >
                <NotificationsContainer
                  setSize={setSize}
                  index={index}
                  containerRef={rootRef}
                  notification={notifications[index]}
                  testID={testID}
                  onClose={onClose}
                  eventId={eventId}
                  markAndPublishNotificationsAsRead={
                    markAndPublishNotificationsAsRead
                  }
                  onNotificationVisible={onNotificationVisible}
                  surveyUrl={surveyUrl}
                />
              </li>
            );
          }}
        </List>
      )}
    </AutoSizer>
  );
};

NotificationsContainer.propTypes = {
  notification: PropTypes.shape({
    id: PropTypes.string,
    metadata: PropTypes.shape({
      entity: PropTypes.shape({
        entityId: PropTypes.string
      })
    }),
    publishedAt: PropTypes.string
  }),
  testID: PropTypes.string,
  onClose: PropTypes.func,
  eventId: PropTypes.string,
  markAndPublishNotificationsAsRead: PropTypes.func,
  containerRef: PropTypes.object,
  onNotificationVisible: PropTypes.func,
  surveyUrl: PropTypes.string,
  setSize: PropTypes.func,
  index: PropTypes.number
};

NotificationFlyoutContent.propTypes = {
  testID: PropTypes.string,
  content: PropTypes.arrayOf(PropTypes.shape(notificationPropType)),
  onClose: PropTypes.func,
  markAndPublishNotificationsAsRead: PropTypes.func,
  rootRef: PropTypes.object,
  onNotificationVisible: PropTypes.func,
  eventId: PropTypes.string,
  surveyUrl: PropTypes.string
};

NotificationFlyoutContent.defaultProps = {
  testID: 'notificationFlyout-content',
  rootRef: {}
};

export default NotificationFlyoutContent;
